import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import type { CorrelationGraph, DobermannReport, DobermannSettings, DobermannSighting } from '../api/types'
import { CategoryBars, CountSeries, formatBytes } from '../components/charts'
import { Card, EmptyState, ErrorBanner, formatDateTime, formatDay, Spinner, StatCard } from '../components/common'
import { DataTable, type Column } from '../components/table'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * Dobermann: la guardia che confronta chi fa traffico con chi è censito.
 *
 * L'operatore dichiara le subnet da proteggere; il giro orario incrocia i
 * flussi con la CMDB, e un indirizzo attivo mai censito diventa un
 * avvistamento. La pagina è divisa come il lavoro: prima ciò che abbaia
 * (i nuovi), poi le decisioni prese (ignorati), poi i lieti fini (censiti —
 * la prova che la guardia serve).
 */
export default function Dobermann() {
  const dati = useAsync(() => api.dobermann(), [])
  const [errore, setErrore] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  async function scansiona() {
    setBusy(true)
    setErrore(null)
    try {
      await api.dobermannScan()
      await dati.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  async function imposta(enabled: boolean, intervallo: number) {
    setBusy(true)
    setErrore(null)
    try {
      await api.dobermannConfigura(enabled, intervallo)
      await dati.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  if (dati.loading && !dati.data) return <Spinner />

  const nuovi = dati.data?.counts['nuovo'] ?? 0
  const conf = dati.data?.settings
  const inServizio = Boolean(conf?.enabled && (dati.data?.watches.length ?? 0) > 0)

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">
            Dobermann
            {nuovi > 0 && <span className="dob-badge">{nuovi}</span>}
          </h1>
          <p className="page__subtitle">
            {t('individua gli asset non censiti correlando le evidenze raccolte da inventory, discovery e flussi NetFlow.')}
          </p>
        </div>
        <div className="toolbar">
          {/* Lo stato «in servizio / a riposo» accanto ai comandi. L'antifurto
              lampeggiante vero — led rosso e faccia del cane — vive nella barra
              in alto, visibile da ogni pagina; qui basta la parola accanto
              all'interruttore che la governa. */}
          <span className={`dob-stato${inServizio ? ' dob-stato--on' : ''}`}>
            {inServizio ? t('In servizio') : t('A riposo')}
          </span>
          {conf && (
            <select
              value={conf.interval_minutes}
              onChange={(e) => void imposta(conf.enabled, Number(e.target.value))}
              disabled={busy}
              aria-label={t('Ogni quanto fa il giro')}
              title={t('Ogni quanto fa il giro')}
            >
              {[5, 10, 15, 30, 60, 180, 360, 1440].map((m) => (
                <option key={m} value={m}>
                  {m < 60 ? t('ogni {n} min', { n: m }) : t('ogni {n} h', { n: m / 60 })}
                </option>
              ))}
            </select>
          )}
          {conf && (
            <button type="button" className={conf.enabled ? 'secondary' : ''}
                    onClick={() => void imposta(!conf.enabled, conf.interval_minutes)}
                    disabled={busy}>
              {conf.enabled ? t('Disattiva la guardia') : t('Attiva la guardia')}
            </button>
          )}
          <button type="button" onClick={scansiona} disabled={busy}>
            {t('Fai il giro adesso')}
          </button>
        </div>
      </header>

      <ErrorBanner error={dati.error} />
      <ErrorBanner error={errore} />

      {dati.data && (
        <>
          <Riepilogo dati={dati.data} />
          <StatisticheDobermann />
          <RegoleDobermann settings={dati.data.settings} onCambiato={() => void dati.reload()} />
          <ImpostazioniNotificheDobermann />
          <Watches
            watches={dati.data.watches}
            onCambiato={() => void dati.reload()}
          />
          <Avvistamenti
            sightings={dati.data.sightings}
            haWatch={dati.data.watches.length > 0}
            onCambiato={() => void dati.reload()}
          />
        </>
      )}
    </>
  )
}

// ------------------------------------------------- il riepilogo in cima

function Riepilogo({ dati }: { dati: DobermannReport }) {
  const c = dati.counts
  const ultimo = dati.settings.last_scan_at
  return (
    <div className="venv-kpi dob-riepilogo">
      <div className="venv-kpi__tile">
        <span className="venv-kpi__value">{dati.watches.length}</span>
        <span className="venv-kpi__label">{t('Subnet sorvegliate')}</span>
      </div>
      <div className="venv-kpi__tile">
        <span className={`venv-kpi__value${(c['nuovo'] ?? 0) > 0 ? ' venv-kpi__value--err' : ' venv-kpi__value--ok'}`}>
          {c['nuovo'] ?? 0}
        </span>
        <span className="venv-kpi__label">{t('Nuovi avvistamenti')}</span>
      </div>
      <div className="venv-kpi__tile">
        <span className="venv-kpi__value">{c['ignorato'] ?? 0}</span>
        <span className="venv-kpi__label">{t('Ignorati')}</span>
      </div>
      <div className="venv-kpi__tile">
        <span className="venv-kpi__value venv-kpi__value--ok">{c['censito'] ?? 0}</span>
        <span className="venv-kpi__label">{t('Censiti (lieti fini)')}</span>
      </div>
      <div className="venv-kpi__tile">
        <span className="venv-kpi__value">{ultimo ? formatDateTime(ultimo) : '—'}</span>
        <span className="venv-kpi__label">{t('Ultimo giro')}</span>
        <span className="venv-kpi__sub">
          {dati.settings.enabled
            ? t('ogni {n} min', { n: dati.settings.interval_minutes })
            : t('guardia disattivata')}
        </span>
      </div>
    </div>
  )
}

// ------------------------------------------------- le subnet sorvegliate

function Watches({
  watches,
  onCambiato,
}: {
  watches: { id: string; cidr: string; label: string }[]
  onCambiato: () => void
}) {
  const [cidr, setCidr] = useState('')
  const [label, setLabel] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [importa, setImporta] = useState(false)
  const [testoImport, setTestoImport] = useState('')
  const [esitoImport, setEsitoImport] = useState('')
  const [modifica, setModifica] = useState<{ id: string; cidr: string; label: string } | null>(null)

  async function aggiungi() {
    if (!cidr.trim()) return
    setBusy(true)
    setErrore(null)
    try {
      await api.dobermannAddWatch(cidr.trim(), label.trim())
      setCidr('')
      setLabel('')
      onCambiato()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  async function rimuovi(id: string) {
    if (!window.confirm(t('Togliere questa sorveglianza? Smette subito di avvisare. Non si può annullare.'))) return
    setErrore(null)
    try {
      await api.dobermannRemoveWatch(id)
      onCambiato()
    } catch (e) {
      setErrore(e)
    }
  }

  async function salvaModifica() {
    if (!modifica) return
    setBusy(true)
    setErrore(null)
    try {
      await api.dobermannEditWatch(modifica.id, modifica.cidr.trim(), modifica.label.trim())
      setModifica(null)
      onCambiato()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  async function importaElenco() {
    if (!testoImport.trim()) return
    setBusy(true)
    setErrore(null)
    setEsitoImport('')
    try {
      const esito = await api.dobermannImport(testoImport)
      setEsitoImport(t('Importate {a} subnet nuove, {m} aggiornate — {tot} sorvegliate in tutto.', {
        a: esito.aggiunte, m: esito.aggiornate, tot: esito.totale,
      }))
      setTestoImport('')
      setImporta(false)
      onCambiato()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Subnet sorvegliate')}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('reti in cui ogni indirizzo attivo deve corrispondere a un asset censito. Vanno incluse solo le reti sotto controllo, escludendo ambienti come guest e NAT, dove la presenza di indirizzi sconosciuti è prevista e genererebbe segnalazioni non significative.')}
      </p>
      {watches.length > 0 && (
        <div className="dob-watches">
          {watches.map((w) => (
            modifica?.id === w.id ? (
              <span key={w.id} className="dob-watch dob-watch--edit">
                <input className="mono" value={modifica.cidr}
                       onChange={(e) => setModifica({ ...modifica, cidr: e.target.value })}
                       aria-label={t('Subnet (CIDR)')} />
                <input value={modifica.label}
                       onChange={(e) => setModifica({ ...modifica, label: e.target.value })}
                       placeholder={t('es. VLAN server')}
                       aria-label={t('Nome della subnet')} />
                <button type="button" onClick={() => void salvaModifica()} disabled={busy}>
                  {t('Salva')}
                </button>
                <button type="button" className="secondary" onClick={() => setModifica(null)}>
                  {t('Annulla')}
                </button>
              </span>
            ) : (
            <span key={w.id} className="dob-watch">
              <span className="mono">{w.cidr}</span>
              {w.label && <span className="dob-watch__label">{w.label}</span>}
              <button type="button" className="dob-watch__x" title={t('Modifica')}
                      onClick={() => setModifica({ id: w.id, cidr: w.cidr, label: w.label })}>
                ✎
              </button>
              <button type="button" className="dob-watch__x" title={t('Rimuovi')}
                      onClick={() => void rimuovi(w.id)}>
                ×
              </button>
            </span>
            )
          ))}
        </div>
      )}
      <div className="dob-form">
        <input
          value={cidr}
          onChange={(e) => setCidr(e.target.value)}
          placeholder="10.20.0.0/16"
          className="mono"
          aria-label={t('Subnet (CIDR)')}
        />
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t('es. VLAN server')}
          aria-label={t('Nome della subnet')}
        />
        <button type="button" onClick={aggiungi} disabled={busy || !cidr.trim()}>
          {t('Sorveglia')}
        </button>
        <button type="button" className="secondary" onClick={() => setImporta(!importa)}>
          {importa ? t('Chiudi import') : t('Importa elenco')}
        </button>
      </div>
      {importa && (
        <div className="dob-import">
          <textarea
            rows={6}
            value={testoImport}
            onChange={(e) => setTestoImport(e.target.value)}
            placeholder={t('Una rete per riga: 10.20.0.0/16, VLAN server — anche ; o TAB come separatore. Le righe con # sono commenti.')}
          />
          <div className="toolbar">
            <button type="button" onClick={() => void importaElenco()}
                    disabled={busy || !testoImport.trim()}>
              {t('Importa')}
            </button>
            <span className="muted">
              {t('Tutte o nessuna: le righe storte vengono rifiutate col numero di riga.')}
            </span>
          </div>
        </div>
      )}
      {esitoImport && <p className="field__hint">{esitoImport}</p>}
      <ErrorBanner error={errore} />
    </Card>
  )
}

// ------------------------------------------------- gli avvistamenti

function Avvistamenti({
  sightings,
  haWatch,
  onCambiato,
}: {
  sightings: DobermannSighting[]
  haWatch: boolean
  onCambiato: () => void
}) {
  const [errore, setErrore] = useState<unknown>(null)
  const [azzerando, setAzzerando] = useState(false)
  const { principal } = useAuth()
  // L'azzeramento cancella anche decisioni e note: solo gli amministratori.
  const isAdmin =
    principal?.roles.includes('tenant_admin') || principal?.roles.includes('provider_admin')
  const [ispeziona, setIspeziona] = useState<DobermannSighting | null>(null)

  async function azzera() {
    if (!window.confirm(t('Azzerare tutti gli avvistamenti, anche quelli ignorati e censiti con le loro note? Le subnet sorvegliate restano; il prossimo giro ripropone ciò che è ancora non censito.'))) return
    setErrore(null)
    setAzzerando(true)
    try {
      await api.dobermannResetSightings()
      onCambiato()
    } catch (e) {
      setErrore(e)
    } finally {
      setAzzerando(false)
    }
  }
  const [associa, setAssocia] = useState<DobermannSighting | null>(null)

  async function decidi(s: DobermannSighting, stato: string) {
    setErrore(null)
    try {
      const nota = stato === 'ignorato'
        ? window.prompt(t('Cos’è questo indirizzo? (la nota resta accanto alla decisione)'), s.nota) ?? ''
        : s.nota
      await api.dobermannSetState(s.id, stato, nota)
      onCambiato()
    } catch (e) {
      setErrore(e)
    }
  }

  // Le colonne della tabella interrogabile: si ordina e si filtra su ognuna,
  // 25 righe per pagina (aumentabili dal selettore). `value` è il dato su cui
  // si ragiona, `render` ciò che si vede.
  const colonne: Column<DobermannSighting>[] = [
    {
      key: 'stato', header: t('Stato'),
      value: (s) => s.stato,
      render: (s) => <StatoBadge stato={s.stato} />,
    },
    {
      key: 'address', header: t('Indirizzo'),
      value: (s) => s.address,
      render: (s) => <span className="mono">{s.address}</span>,
    },
    {
      key: 'subnet', header: t('Subnet'),
      value: (s) => s.watch_cidr,
      render: (s) => <span className="mono muted">{s.watch_cidr}</span>,
    },
    {
      key: 'first_seen', header: t('Primo traffico'), type: 'date',
      value: (s) => s.first_seen,
      render: (s) => (s.first_seen ? formatDateTime(s.first_seen) : '—'),
    },
    {
      key: 'last_seen', header: t('Ultimo traffico'), type: 'date',
      value: (s) => s.last_seen,
      render: (s) => (s.last_seen ? formatDateTime(s.last_seen) : '—'),
    },
    {
      key: 'bytes', header: t('Volume (24h)'), type: 'number', align: 'right',
      value: (s) => s.bytes_total,
      render: (s) => fmtBytes(s.bytes_total),
    },
    {
      key: 'peers', header: t('Interlocutori'), type: 'number', align: 'right',
      value: (s) => s.peers,
    },
    {
      key: 'nota', header: t('Nota'),
      value: (s) => s.nota,
      render: (s) => <span className="muted">{s.nota || '—'}</span>,
    },
    {
      key: 'azioni', header: '', searchable: false,
      value: () => '',
      render: (s) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <button type="button" className="secondary dob-azione"
                  onClick={(e) => { e.stopPropagation(); setIspeziona(s) }}>
            {t('Ispeziona')}
          </button>
          {s.stato === 'nuovo' && (
            <button type="button" className="secondary dob-azione"
                    onClick={(e) => { e.stopPropagation(); setAssocia(s) }}>
              {t('È un indirizzo di…')}
            </button>
          )}
          {s.stato === 'nuovo' && (
            <button type="button" className="secondary dob-azione"
                    onClick={(e) => { e.stopPropagation(); void decidi(s, 'ignorato') }}>
              {t('Lo conosco, ignora')}
            </button>
          )}
          {s.stato === 'ignorato' && (
            <button type="button" className="secondary dob-azione"
                    onClick={(e) => { e.stopPropagation(); void decidi(s, 'nuovo') }}>
              {t('Riattiva')}
            </button>
          )}
          {s.stato === 'censito' && s.ci_id && (
            <Link to={`/cis/${s.ci_id}`} onClick={(e) => e.stopPropagation()}>{t('Vai al CI')}</Link>
          )}
        </span>
      ),
    },
  ]

  return (
    <Card
      title={t('Avvistamenti ({n})', { n: sightings.length })}
      actions={
        isAdmin && sightings.length > 0 ? (
          <button type="button" className="danger" onClick={() => void azzera()} disabled={azzerando}>
            {azzerando ? t('Azzero…') : t('Azzera avvistamenti')}
          </button>
        ) : undefined
      }
    >
      {!haWatch ? (
        <EmptyState
          title={t('Nessuna subnet sorvegliata')}
          hint={t('Dichiara qui sopra le reti da proteggere: il giro orario confronterà il traffico con l’inventario.')}
        />
      ) : sightings.length === 0 ? (
        <EmptyState
          title={t('Nessun avvistamento')}
          hint={t('Tutto ciò che parla nelle subnet sorvegliate è censito. Il giro passa ogni ora sui flussi delle ultime 24.')}
        />
      ) : (
        <DataTable
          rows={sightings}
          columns={colonne}
          rowKey={(s) => s.id}
          initialSort={{ key: 'last_seen', desc: true }}
        />
      )}
      <p className="field__hint">
        {t('Dobermann confronta il traffico con l’inventario e segnala gli asset non censiti. Con «Ispeziona» ne esplori comunicazioni e porte; quando l’indirizzo entra nel CMDB, viene automaticamente considerato censito. In alternativa, puoi avviare una discovery, censirlo manualmente oppure «Ignora» specificandone il motivo.')}
      </p>
      <ErrorBanner error={errore} />
      {ispeziona && (
        <IspezionaModal avvistamento={ispeziona} onChiudi={() => setIspeziona(null)} />
      )}
      {associa && (
        <AssociaModal
          avvistamento={associa}
          onChiudi={() => setAssocia(null)}
          onFatto={() => { setAssocia(null); onCambiato() }}
        />
      )}
    </Card>
  )
}

/**
 * Il popup «È un indirizzo di…»: cerca il CI a cui l'indirizzo appartiene e, con
 * un clic, lo registra come suo indirizzo aggiuntivo. Chiude il cerchio meglio
 * di «ignora»: scrive il dato mancante invece di zittire il cane, e da lì il
 * traffico futuro verso quell'IP si riconcilia da sé.
 */
function AssociaModal({
  avvistamento,
  onChiudi,
  onFatto,
}: {
  avvistamento: DobermannSighting
  onChiudi: () => void
  onFatto: () => void
}) {
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const risultati = useAsync(
    () => (query.trim().length >= 2 ? api.cis({ q: query.trim(), limit: 20 }) : Promise.resolve([])),
    [query],
  )

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onChiudi() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onChiudi])

  async function scegli(ciId: string) {
    setBusy(ciId)
    setErrore(null)
    try {
      await api.dobermannAssocia(avvistamento.id, ciId)
      onFatto()
    } catch (e) {
      setErrore(e)
      setBusy(null)
    }
  }

  return (
    <div className="dob-modal" role="dialog" aria-modal="true"
         aria-label={t('È un indirizzo di…')}>
      <div className="dob-modal__scrim" onClick={onChiudi} aria-hidden="true" />
      <div className="dob-modal__pannello">
        <div className="dob-modal__testata">
          <div>
            <h2 className="dob-modal__titolo">
              <span className="mono">{avvistamento.address}</span>
            </h2>
            <p className="muted" style={{ margin: 0 }}>
              {t('Scegli il CI di cui questo indirizzo fa parte: lo aggiungo ai suoi indirizzi aggiuntivi e l’avvistamento diventa «censito».')}
            </p>
          </div>
          <button type="button" className="secondary" onClick={onChiudi}>{t('Chiudi')}</button>
        </div>
        <div className="field">
          <input
            type="search"
            autoFocus
            placeholder={t('Cerca un CI per nome o chiave naturale…')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <ErrorBanner error={errore} />
        {query.trim().length < 2 ? (
          <p className="field__hint">{t('Scrivi almeno due caratteri per cercare.')}</p>
        ) : risultati.loading ? (
          <Spinner />
        ) : (risultati.data ?? []).length === 0 ? (
          <p className="field__hint">{t('Nessun CI trovato.')}</p>
        ) : (
          <ul className="dob-associa__lista">
            {(risultati.data ?? []).map((ci) => (
              <li key={ci.id}>
                <button type="button" className="dob-associa__voce" disabled={busy !== null}
                        onClick={() => void scegli(ci.id)}>
                  <span className="dob-associa__nome">{ci.name ?? ci.primary_key}</span>
                  <span className="mono muted">
                    {String((ci.attributes as Record<string, unknown>)?.mgmt_ip ?? ci.ci_class ?? ci.ci_type)}
                  </span>
                  {busy === ci.id && <span className="muted"> {t('Associo…')}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function StatoBadge({ stato }: { stato: string }) {
  if (stato === 'nuovo') return <span className="badge badge--error">{t('nuovo')}</span>
  if (stato === 'ignorato') return <span className="badge">{t('ignorato')}</span>
  return <span className="badge badge--ok">{t('censito')}</span>
}

function fmtBytes(bytes: number): string {
  if (!bytes) return '0'
  const g = bytes / 1024 ** 3
  if (g >= 1) return `${g.toFixed(g >= 10 ? 0 : 1)} GB`
  const m = bytes / 1024 ** 2
  if (m >= 1) return `${m.toFixed(m >= 10 ? 0 : 1)} MB`
  return `${Math.round(bytes / 1024)} KB`
}

// ------------------------------------------------- l'ispezione (popup)

const PROTOCOLLI: Record<number, string> = { 1: 'ICMP', 6: 'TCP', 17: 'UDP' }

function nomeProto(n: number): string {
  return PROTOCOLLI[n] ?? String(n)
}

/**
 * Il popup dell'ispezione: con chi parla l'indirizzo sospetto, in tabella o
 * in mappa (switch in alto). Un popup a sé e non la pagina della mappa
 * flussi: quella è lo strumento per esplorare e costruire business service,
 * e atterrarci nel mezzo di un'indagine confonde il compito. Da qui la mappa
 * completa resta a un click, per chi vuole andare oltre i vicini diretti.
 */
function IspezionaModal({
  avvistamento,
  onChiudi,
}: {
  avvistamento: DobermannSighting
  onChiudi: () => void
}) {
  const [vista, setVista] = useState<'tabella' | 'mappa'>('tabella')
  const [finestra, setFinestra] = useState('24h')
  const grafo = useAsync<CorrelationGraph>(
    () => api.correlationGraph(avvistamento.address, { levels: 1, window: finestra }),
    [avvistamento.address, finestra],
  )

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onChiudi()
    }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onChiudi])

  const d = grafo.data
  const seme = avvistamento.address

  // Gli archi visti dal seme: verso + interlocutore, ordinati per volume.
  const righe = useMemo(() => {
    if (!d) return []
    const perNodo = new Map(d.nodes.map((n) => [n.address, n]))
    return d.edges
      .map((e) => {
        const esce = e.src === seme
        const peer = esce ? e.dst : e.src
        return { ...e, esce, peer, nodo: perNodo.get(peer) }
      })
      .filter((r) => r.peer !== seme)
      .sort((a, b) => b.bytes - a.bytes)
  }, [d, seme])

  return (
    <div className="dob-modal" role="dialog" aria-modal="true"
         aria-label={t('Ispezione dell’indirizzo')}>
      <div className="dob-modal__scrim" onClick={onChiudi} aria-hidden="true" />
      <div className="dob-modal__pannello">
        <div className="dob-modal__testata">
          <div>
            <h2 className="dob-modal__titolo">
              <span className="mono">{avvistamento.address}</span>
              <span className="muted"> · {avvistamento.watch_cidr}</span>
            </h2>
            <p className="dob-modal__sotto">
              {t('Con chi parla, su che porte, con che volumi — dai flussi raccolti.')}
            </p>
          </div>
          <div className="dob-modal__comandi">
            <select value={finestra} onChange={(e) => setFinestra(e.target.value)}
                    aria-label={t('Finestra temporale')}>
              <option value="24h">{t('Ultime 24 ore')}</option>
              <option value="7d">{t('Ultimi 7 giorni')}</option>
            </select>
            <div className="vmap-vista">
              <button type="button"
                      className={`vmap-vista__btn${vista === 'tabella' ? ' vmap-vista__btn--on' : ''}`}
                      onClick={() => setVista('tabella')}>
                {t('Tabella')}
              </button>
              <button type="button"
                      className={`vmap-vista__btn${vista === 'mappa' ? ' vmap-vista__btn--on' : ''}`}
                      onClick={() => setVista('mappa')}>
                {t('Mappa')}
              </button>
            </div>
            <button type="button" className="secondary" onClick={onChiudi}>
              {t('Chiudi')}
            </button>
          </div>
        </div>

        {grafo.loading && !d ? (
          <Spinner />
        ) : grafo.error ? (
          <ErrorBanner error={grafo.error} />
        ) : !d || righe.length === 0 ? (
          <p className="muted">
            {t('Nessuna conversazione nella finestra scelta: l’indirizzo ha parlato prima, o il traffico non passa dagli esportatori.')}
          </p>
        ) : vista === 'tabella' ? (
          <TabellaIspezione righe={righe} />
        ) : (
          <MappaIspezione seme={seme} righe={righe} />
        )}

        <p className="field__hint dob-modal__piede">
          {t('Interlocutori diretti (un livello). Per spingerti oltre:')}{' '}
          <Link to={`/flows/map?seed=${encodeURIComponent(seme)}`} onClick={onChiudi}>
            {t('apri l’esplorazione completa')}
          </Link>
        </p>
      </div>
    </div>
  )
}

type RigaIspezione = {
  esce: boolean
  peer: string
  nodo?: { ci_id: string | null; name: string | null; ci_class: string | null }
  dst_port: number
  protocol: number
  bytes: number
  flows: number
}

function TabellaIspezione({ righe }: { righe: RigaIspezione[] }) {
  return (
    <div className="dob-modal__corpo">
      <DataTable
        rows={righe}
        rowKey={(r, i) => `${r.esce ? 'out' : 'in'}|${r.peer}|${r.dst_port}|${r.protocol}|${i}`}
        initialSort={{ key: 'bytes', dir: 'desc' }}
        columns={[
          {
            key: 'direction', header: t('Verso'), value: (r) => (r.esce ? t('verso') : t('da')),
            render: (r) => <>{r.esce ? '→' : '←'} {r.esce ? t('verso') : t('da')}</>,
          },
          {
            key: 'peer', header: t('Interlocutore'),
            value: (r) => (r.nodo?.ci_id ? `${r.nodo.name || r.peer} ${r.peer}` : `${r.peer} ${t('non censito')}`),
            render: (r) => (
              r.nodo?.ci_id ? (
                <>
                  <Link to={`/cis/${r.nodo.ci_id}`}>{r.nodo.name || r.peer}</Link>{' '}
                  <span className="mono muted">{r.peer}</span>
                </>
              ) : (
                <>
                  <span className="mono">{r.peer}</span>{' '}
                  <span className="badge badge--warn">{t('non censito')}</span>
                </>
              )
            ),
          },
          { key: 'dst_port', header: t('Porta'), mono: true, value: (r) => r.dst_port || null },
          { key: 'protocol', header: t('Protocollo'), value: (r) => nomeProto(r.protocol) },
          { key: 'bytes', header: t('Volume'), type: 'number', value: (r) => r.bytes, render: (r) => fmtBytes(r.bytes) },
          { key: 'flows', header: t('Flussi'), type: 'number', mono: true },
        ]}
      />
    </div>
  )
}

/**
 * La mappa del popup: il sospetto al centro, gli interlocutori in cerchio.
 * Verde chi è censito (il nome lo dice), grigio chi non lo è; lo spessore
 * dell'arco è il volume. Un livello solo, leggibile: per l'esplorazione
 * profonda c'è il collegamento sotto.
 */
function MappaIspezione({ seme, righe }: { seme: string; righe: RigaIspezione[] }) {
  const W = 720
  const H = 460
  const cx = W / 2
  const cy = H / 2

  // Un nodo per interlocutore (gli archi su porte diverse si sommano).
  const peers = useMemo(() => {
    const per = new Map<string, { bytes: number; nodo?: RigaIspezione['nodo']; esce: boolean; riceve: boolean }>()
    for (const r of righe) {
      const p = per.get(r.peer) ?? { bytes: 0, nodo: r.nodo, esce: false, riceve: false }
      p.bytes += r.bytes
      if (r.esce) p.esce = true
      else p.riceve = true
      per.set(r.peer, p)
    }
    return [...per.entries()].sort((a, b) => b[1].bytes - a[1].bytes).slice(0, 24)
  }, [righe])

  const maxBytes = Math.max(1, ...peers.map(([, p]) => p.bytes))
  const raggio = Math.min(cx, cy) - 70

  return (
    <div className="dob-modal__corpo">
      <svg viewBox={`0 0 ${W} ${H}`} className="vmap-mappa__svg" role="img"
           aria-label={t('Mappa degli interlocutori')}>
        {peers.map(([addr, p], i) => {
          const a = (2 * Math.PI * i) / peers.length - Math.PI / 2
          const x = cx + raggio * Math.cos(a)
          const y = cy + raggio * Math.sin(a)
          const spessore = 1 + 3 * (p.bytes / maxBytes)
          const censito = Boolean(p.nodo?.ci_id)
          return (
            <g key={addr}>
              <line x1={cx} y1={cy} x2={x} y2={y}
                    stroke={censito ? 'var(--ok, #12b76a)' : 'var(--text-muted, #98a2b3)'}
                    strokeWidth={spessore} opacity={0.5} />
              <g className="vmap-mappa__nodo" transform={`translate(${x} ${y})`}
                 onClick={() => { if (p.nodo?.ci_id) window.location.href = `/cis/${p.nodo.ci_id}` }}>
                <title>{addr}{p.nodo?.name ? ` — ${p.nodo.name}` : ''} · {fmtBytes(p.bytes)}</title>
                <circle r={8} fill={censito ? 'var(--ok, #12b76a)' : '#98a2b3'}
                        stroke="var(--bg, #fff)" strokeWidth={1.5} />
                <text x={0} y={y > cy ? 22 : -14} textAnchor="middle"
                      className="vmap-mappa__etichetta">
                  {p.nodo?.name || addr}
                </text>
              </g>
            </g>
          )
        })}
        {/* il sospetto, al centro e sopra tutto */}
        <g transform={`translate(${cx} ${cy})`}>
          <circle r={22} fill="var(--error, #d92d20)" opacity={0.15} />
          <circle r={12} fill="var(--error, #d92d20)" stroke="var(--bg, #fff)" strokeWidth={2} />
          <text x={0} y={38} textAnchor="middle" className="venv-mappa__host mono">{seme}</text>
        </g>
      </svg>
      <div className="vmap-mappa__legenda">
        <span className="vmap-mappa__voce"><span className="venv-legenda" style={{ background: 'var(--error)' , borderRadius: '50%' }} />{t('il sospetto')}</span>
        <span className="vmap-mappa__voce"><span className="venv-legenda" style={{ background: 'var(--ok)', borderRadius: '50%' }} />{t('interlocutore censito')}</span>
        <span className="vmap-mappa__voce"><span className="venv-legenda" style={{ background: '#98a2b3', borderRadius: '50%' }} />{t('non censito')}</span>
        <span className="muted">{t('spessore = volume')}</span>
      </div>
    </div>
  )
}

// ------------------------------------------------- le statistiche

function StatisticheDobermann() {
  const [giorni, setGiorni] = useState(30)
  const dati = useAsync(() => api.dobermannStats(giorni), [giorni])

  const s = dati.data
  const totale = s ? s.summary.new_window : 0
  const picco = s ? s.series.reduce((best, p) => (p.new > best.new ? p : best), s.series[0]) : null
  const giorniConNuovi = s ? s.series.filter((p) => p.new > 0).length : 0
  const totStati = s ? (s.summary.nuovo + s.summary.ignorato + s.summary.censito) : 0
  const quota = (n: number) => (totStati ? (n / totStati) * 100 : 0)
  const maxFlussi = s ? Math.max(1, ...s.top_talkers.map((tk) => tk.flow_count)) : 1

  return (
    <Card title={t('Statistiche')}
          actions={(
            <label className="muted" style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
              {t('Periodo')}
              <select value={giorni} onChange={(e) => setGiorni(Number(e.target.value))} aria-label={t('Periodo')}>
                <option value={7}>{t('7 giorni')}</option>
                <option value={30}>{t('30 giorni')}</option>
                <option value={90}>{t('90 giorni')}</option>
              </select>
            </label>
          )}>
      {dati.loading && !s ? <Spinner /> : dati.error ? <ErrorBanner error={dati.error} /> : s ? (
        <>
          <div className="scanstats__kpi">
            <StatCard label={t('Nuovi nel periodo')} value={s.summary.new_window}
                      hint={t('{n} giorni con avvistamenti su {tot}', { n: giorniConNuovi, tot: s.series.length })}
                      tone={s.summary.new_window > 0 ? 'warn' : undefined} />
            <StatCard label={t('Nuovi (7g)')} value={s.summary.new_7d} />
            <StatCard label={t('Da decidere')} value={s.summary.nuovo} hint={t('ancora «nuovi»')}
                      tone={s.summary.nuovo > 0 ? 'error' : 'ok'} />
            <StatCard label={t('Ignorati')} value={s.summary.ignorato} />
            <StatCard label={t('Censiti')} value={s.summary.censito} tone="ok" hint={t('{n} subnet sorvegliate', { n: s.summary.watches })} />
          </div>

          <h4 className="dob-stat__titolo">{t('Nuovi avvistamenti per giorno')}</h4>
          <CountSeries points={s.series.map((p) => ({ date: p.date, value: p.new }))}
                       tone="warn" label={t('Nuovi avvistamenti per giorno')}
                       emptyHint={t('Nessun avvistamento nel periodo.')} />
          {totale > 0 && picco && (
            <p className="dob-stat__nota">
              {t('{tot} in {g} giorni, in media {m} al giorno; il picco il {data} con {n}.', {
                tot: totale, g: s.series.length, m: (totale / s.series.length).toFixed(1), data: formatDay(picco.date), n: picco.new,
              })}
            </p>
          )}

          <div className="dob-stat">
            <div>
              <h4 className="dob-stat__titolo">{t('Come stanno oggi')}</h4>
              {totStati === 0 ? <p className="text-muted" style={{ margin: 0 }}>{t('Nessun avvistamento.')}</p> : (
                <>
                  <div className="dob-quote" role="img" aria-label={t('Avvistamenti per stato')}>
                    {s.summary.nuovo > 0 && <div className="dob-quote__seg--error" style={{ width: `${quota(s.summary.nuovo)}%` }} title={`${t('nuovo')}: ${s.summary.nuovo}`} />}
                    {s.summary.ignorato > 0 && <div className="dob-quote__seg--muted" style={{ width: `${quota(s.summary.ignorato)}%` }} title={`${t('ignorato')}: ${s.summary.ignorato}`} />}
                    {s.summary.censito > 0 && <div className="dob-quote__seg--ok" style={{ width: `${quota(s.summary.censito)}%` }} title={`${t('censito')}: ${s.summary.censito}`} />}
                  </div>
                  <ul className="dob-quote__legenda" style={{ margin: '8px 0 0', padding: 0 }}>
                    <li><span className="dob-quote__punto dob-quote__punto--error" />{t('nuovo')} <strong>{s.summary.nuovo}</strong> <span className="muted">({Math.round(quota(s.summary.nuovo))}%)</span></li>
                    <li><span className="dob-quote__punto dob-quote__punto--muted" />{t('ignorato')} <strong>{s.summary.ignorato}</strong> <span className="muted">({Math.round(quota(s.summary.ignorato))}%)</span></li>
                    <li><span className="dob-quote__punto dob-quote__punto--ok" />{t('censito')} <strong>{s.summary.censito}</strong> <span className="muted">({Math.round(quota(s.summary.censito))}%)</span></li>
                  </ul>
                  <p className="dob-stat__nota">{t('Un avvistamento «nuovo» aspetta una decisione: censirlo o ignorarlo.')}</p>
                </>
              )}
            </div>
            <div>
              <h4 className="dob-stat__titolo">{t('Per subnet sorvegliata')}</h4>
              <CategoryBars data={Object.entries(s.by_watch).map(([k, v]) => ({ key: k, label: k, value: v }))}
                            emptyHint={t('Nessun avvistamento.')} />
            </div>
            <div>
              <h4 className="dob-stat__titolo">{t('I più parlanti')}</h4>
              {s.top_talkers.length === 0 ? (
                <p className="text-muted" style={{ margin: 0 }}>{t('Nessun avvistamento attivo.')}</p>
              ) : (
                <table className="dob-parlanti">
                  <tbody>
                    {s.top_talkers.map((tk) => (
                      <tr key={tk.id}>
                        <td className="mono">{tk.address}<div className="scanstats__meta">{tk.watch_cidr}</div></td>
                        <td style={{ width: '38%' }}>
                          <div className="dob-parlanti__traccia" title={t('{n} flussi', { n: tk.flow_count })}>
                            <div className="dob-parlanti__pieno" style={{ width: `${Math.max(2, (tk.flow_count / maxFlussi) * 100)}%` }} />
                          </div>
                        </td>
                        <td className="dob-parlanti__num">{t('{n} flussi', { n: tk.flow_count })}<div className="scanstats__meta">{formatBytes(tk.bytes_total)}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="dob-stat__nota">{t('Gli avvistamenti «nuovi» che hanno mosso più flussi nell’ultimo giro: chi merita un’occhiata per primo.')}</p>
            </div>
          </div>
        </>
      ) : null}
    </Card>
  )
}

// ------------------------------------------------- impostazioni e notifiche

function ImpostazioniNotificheDobermann() {
  const dati = useAsync(() => api.dobermannNotifications(), [])
  const [email, setEmail] = useState<boolean | null>(null)
  const [minFlows, setMinFlows] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  const d = dati.data
  if (!d) return dati.error ? <Card title={t('Impostazioni e notifiche')}><ErrorBanner error={dati.error} /></Card> : null

  const vEmail = email ?? d.email
  const vMin = minFlows ?? d.min_flows

  async function salva() {
    setBusy(true); setErrore(null)
    try {
      await api.setDobermannNotifications({ email: vEmail, min_flows: vMin })
      setEmail(null); setMinFlows(null)
      await dati.reload()
    } catch (e) { setErrore(e) } finally { setBusy(false) }
  }

  return (
    <Card title={t('Impostazioni e notifiche')}>
      <div className="field">
        <label className="check">
          <input type="checkbox" checked={vEmail} onChange={(e) => setEmail(e.target.checked)} />
          {t('Avvisami via email sui nuovi avvistamenti')}
        </label>
        {vEmail && !d.recipients_configured && (
          <p className="field__hint">
            {t('Nessun destinatario configurato: aggiungilo in Impostazioni → Posta in uscita, o l’avviso non arriva a nessuno.')}
          </p>
        )}
      </div>
      <div className="field">
        <label htmlFor="dob-minflows">{t('Avvisa solo sopra questa soglia di flussi')}</label>
        <input id="dob-minflows" type="number" min={0} value={vMin} style={{ width: 120 }}
               onChange={(e) => setMinFlows(Math.max(0, Number(e.target.value) || 0))} />
        <p className="field__hint">
          {t('0 = avvisa per tutti. Alzala per ignorare il pacchetto vagante singolo (un device mal configurato che sonda una volta). L’avvistamento resta comunque registrato.')}
        </p>
      </div>
      <div className="toolbar">
        <button type="button" onClick={salva} disabled={busy}>{t('Salva')}</button>
      </div>
      <ErrorBanner error={errore} />
    </Card>
  )
}


// ------------------------------------------------- le regole: chi conta

const PROTOCOLLI_ESCLUDIBILI: { n: number; label: string }[] = [
  { n: 1, label: 'ICMP' }, { n: 2, label: 'IGMP' }, { n: 17, label: 'UDP' }, { n: 6, label: 'TCP' },
]

/**
 * Chi conta come «parla». Un indirizzo pingato che non risponde compare nei
 * flussi solo come destinazione: non c'è nessuno dietro, e segnalarlo sarebbe
 * abbaiare al vuoto. Per questo di norma contano solo gli indirizzi che
 * **mandano** pacchetti; e si possono escludere protocolli e porte (il ping
 * di un monitoraggio, un broadcast di servizio).
 */
function RegoleDobermann({ settings, onCambiato }: { settings: DobermannSettings; onCambiato: () => void }) {
  const [direzione, setDirezione] = useState<'source' | 'both'>(settings.direction ?? 'source')
  const noti = PROTOCOLLI_ESCLUDIBILI.map((p) => p.n)
  const [protocolli, setProtocolli] = useState<number[]>((settings.exclude_protocols ?? []).filter((n) => noti.includes(n)))
  // I protocolli senza casella, per numero: il 249 di certi firewall, il GRE (47)…
  const [altri, setAltri] = useState((settings.exclude_protocols ?? []).filter((n) => !noti.includes(n)).join(', '))
  const [porte, setPorte] = useState((settings.exclude_ports ?? []).join(', '))
  const [errore, setErrore] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const numeri = (testo: string) => testo.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean).map(Number).filter((n) => !Number.isNaN(n))
  const tuttiProtocolli = [...protocolli, ...numeri(altri).filter((n) => !protocolli.includes(n))]
  const cambiato = direzione !== (settings.direction ?? 'source')
    || [...tuttiProtocolli].sort((a, b) => a - b).join(',') !== [...(settings.exclude_protocols ?? [])].sort((a, b) => a - b).join(',')
    || numeri(porte).join(',') !== (settings.exclude_ports ?? []).join(',')

  async function salva() {
    setBusy(true)
    setErrore(null)
    try {
      await api.dobermannConfigura(settings.enabled, settings.interval_minutes, { direction: direzione, exclude_protocols: tuttiProtocolli, exclude_ports: numeri(porte) })
      onCambiato()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Chi conta come «parla»')}>
      <ErrorBanner error={errore} />
      <p className="muted" style={{ marginTop: 0 }}>
        {t('un IP è considerato attivo quando viene osservato come sorgente di traffico. Se compare solo come destinazione nei flussi non dimostra la presenza di un asset.')}
      </p>
      <div className="eagle-form" style={{ maxWidth: 'none' }}>
        <label className="field" style={{ maxWidth: 420 }}>
          <span className="field__label">{t('Direzione')}</span>
          <select value={direzione} onChange={(e) => setDirezione(e.target.value as 'source' | 'both')} disabled={busy}>
            <option value="source">{t('Solo chi manda pacchetti (consigliato)')}</option>
            <option value="both">{t('Anche chi li riceve soltanto')}</option>
          </select>
        </label>
        <fieldset className="field">
          <legend className="field__label">{t('Protocolli da non contare')}</legend>
          {PROTOCOLLI_ESCLUDIBILI.map((p) => (
            <label key={p.n} className="eagle-interruttore" style={{ marginRight: 14 }}>
              <input type="checkbox" checked={protocolli.includes(p.n)} disabled={busy}
                     onChange={() => setProtocolli(protocolli.includes(p.n) ? protocolli.filter((x) => x !== p.n) : [...protocolli, p.n])} />
              {' '}{p.label}
            </label>
          ))}
        </fieldset>
        <label className="field" style={{ maxWidth: 420 }}>
          <span className="field__label">{t('Altri protocolli, per numero')}</span>
          <input value={altri} onChange={(e) => setAltri(e.target.value)} placeholder="249, 47" className="mono" disabled={busy} />
          <span className="muted eagle-piccolo">{t('Il numero del protocollo IP come lo mostra la scheda dell’avvistamento (per esempio 249 su certi firewall).')}</span>
        </label>
        <label className="field" style={{ maxWidth: 420 }}>
          <span className="field__label">{t('Porte di destinazione da non contare')}</span>
          <input value={porte} onChange={(e) => setPorte(e.target.value)} placeholder="137, 138, 1900" className="mono" disabled={busy} />
          <span className="muted eagle-piccolo">{t('Separate da virgola. Un flusso verso una di queste porte non fa di nessuno un avvistamento.')}</span>
        </label>
        <div className="eagle-form__azioni">
          <button type="button" onClick={salva} disabled={busy || !cambiato}>{busy ? t('Salvo…') : t('Salva le regole')}</button>
        </div>
      </div>
      <p className="muted eagle-piccolo" style={{ marginBottom: 0 }}>{t('Le regole valgono dal prossimo giro; gli avvistamenti già registrati restano finché non li azzeri.')}</p>
    </Card>
  )
}
